import { ChatMessage } from "../api";
import { PromptTemplate } from "../models/PromptTemplate";

/**
 * Strips embedded prompt-template content from user messages loaded out of
 * conversation history, replacing it with the original slash-command form
 * (e.g. "/prd some user input").
 *
 * When a user sends a templated message the UI expands the slash command into
 * "[full template body]\n\n[user input]" before calling the backend.  The
 * backend persists that expanded form.  On reload we must reverse the
 * substitution so the raw template text is never visible to the user.
 *
 * The function is **idempotent** – messages that have already been stripped
 * (or that never contained template content) are returned unchanged.
 *
 * Performance: if no message was actually modified the *original* array
 * reference is returned so callers can use reference-equality to skip
 * unnecessary state updates.
 */
export const stripTemplateContent = (
  messages: ChatMessage[],
  knownTemplates: PromptTemplate[]
): ChatMessage[] => {
  if (!knownTemplates || knownTemplates.length === 0) return messages;

  let anyChanged = false;

  const result = messages.map((msg) => {
    if (msg.role !== "user") return msg;

    for (const tpl of knownTemplates) {
      if (!tpl.content) continue;
      const prefix = tpl.content + "\n\n";
      if (msg.content.startsWith(prefix)) {
        anyChanged = true;
        const userInput = msg.content.slice(prefix.length);
        return {
          ...msg,
          content: userInput ? `/${tpl.promptKey} ${userInput}` : `/${tpl.promptKey}`,
        };
      }
    }

    return msg;
  });

  // Return the original reference when nothing changed so callers can do a
  // cheap reference-equality check to skip redundant state updates.
  return anyChanged ? result : messages;
};
